<template>
  <section v-if="featureEnabled" ref="rootEl" class="jflt" data-testid="job-family-levels-table">
    <!-- Header: title/subtitle block + "+ Add Level" action. -->
    <header class="jflt__header">
      <div class="jflt__title-block">
        <h2 class="jflt__title">Levels</h2>
        <p class="jflt__subtitle">
          Ordered career ladder for this Job Family. Drag rows to reorder.
        </p>
      </div>
      <div v-if="!readOnly" class="jflt__actions">
        <button
          type="button"
          class="jflt__add"
          data-testid="add-level"
          :disabled="busy"
          @click="onAddLevel"
        >
          <svg
            class="jflt__add-icon"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10.0002 4.16699V15.8337M4.16699 10.0003H15.8337"
              stroke="currentColor"
              stroke-width="1.66667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>Add Level</span>
        </button>
        <AskIQButton
          class="jflt__suggest"
          variant="light"
          label="Suggest"
          :height="40"
          :disabled="busy"
          data-testid="suggest-levels"
          @click="onSuggest"
        />
      </div>
    </header>

    <!-- Post-Suggestion review banner (ER-11793): shown above the table after a
         successful Suggest, carrying the session-scoped Undo action and a close (×). -->
    <div
      v-if="suggestionActive"
      class="jflt__suggestion-banner"
      role="status"
      data-testid="suggestion-banner"
    >
      <span class="jflt__suggestion-icon" aria-hidden="true">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="10" cy="10" r="9" stroke="#7f56d9" stroke-width="2" />
          <path
            d="M10 9V14M10 6.5V6.6"
            stroke="#7f56d9"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </span>
      <div class="jflt__suggestion-content">
        <p class="jflt__suggestion-title">IQ suggested these levels</p>
        <p class="jflt__suggestion-subtitle">
          You can improve suggestions by adding context to the Job Family
        </p>
        <button
          type="button"
          class="jflt__suggestion-undo"
          data-testid="undo-suggestions"
          :disabled="undoSuggestionBusy"
          :aria-busy="undoSuggestionBusy ? 'true' : 'false'"
          @click="onUndoSuggestions"
        >
          Undo Suggestions
        </button>
      </div>
      <button
        type="button"
        class="jflt__suggestion-close"
        aria-label="Dismiss suggestions"
        data-testid="dismiss-suggestions"
        @click="onDismissSuggestion"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M18 6 6 18M6 6l12 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <div class="jflt__body">
      <table v-if="displayRows.length" class="jflt__table" :aria-busy="busy ? 'true' : 'false'">
        <caption class="jflt__visually-hidden">
          Job family levels
        </caption>
        <colgroup>
          <col class="jflt__col-handle" />
          <col class="jflt__col-rank" />
          <col class="jflt__col-name" />
          <col class="jflt__col-descriptor" />
          <col class="jflt__col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" class="jflt__th jflt__th--handle">
              <span class="jflt__visually-hidden">Reorder</span>
            </th>
            <th scope="col" class="jflt__th">#</th>
            <th scope="col" class="jflt__th">Name</th>
            <th scope="col" class="jflt__th">Descriptor</th>
            <th scope="col" class="jflt__th jflt__th--actions">
              <span class="jflt__visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in displayRows"
            :key="row.id"
            class="jflt__row"
            :class="{
              'jflt__row--dragging': draggedId === row.id,
              'jflt__row--drag-over': dragOverId === row.id,
            }"
            @dragover.prevent="onDragOver(row)"
            @drop.prevent="onDrop(row)"
          >
            <td class="jflt__cell jflt__cell--handle">
              <button
                v-if="!readOnly"
                type="button"
                class="jflt__handle"
                :aria-label="`Reorder ${row.name}`"
                :aria-disabled="busy ? 'true' : 'false'"
                :draggable="!busy"
                data-testid="drag-handle"
                @dragstart="onDragStart($event, row)"
                @dragend="onDragEnd"
              >
                <svg
                  class="jflt__grid-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle cx="5" cy="5" r="1.25" fill="currentColor" />
                  <circle cx="10" cy="5" r="1.25" fill="currentColor" />
                  <circle cx="15" cy="5" r="1.25" fill="currentColor" />
                  <circle cx="5" cy="10" r="1.25" fill="currentColor" />
                  <circle cx="10" cy="10" r="1.25" fill="currentColor" />
                  <circle cx="15" cy="10" r="1.25" fill="currentColor" />
                  <circle cx="5" cy="15" r="1.25" fill="currentColor" />
                  <circle cx="10" cy="15" r="1.25" fill="currentColor" />
                  <circle cx="15" cy="15" r="1.25" fill="currentColor" />
                </svg>
              </button>
              <span v-else class="jflt__handle-spacer" aria-hidden="true" />
            </td>

            <td class="jflt__cell jflt__cell--rank" data-testid="level-rank">
              {{ row.ordinal }}
            </td>

            <td class="jflt__cell jflt__cell--name" data-testid="level-name">
              <span class="jflt__name-text">{{ row.name }}</span>
              <span
                v-if="rowFlag(row.id)"
                class="jflt__badge"
                :class="`jflt__badge--${rowFlag(row.id)}`"
                :data-testid="`level-badge-${rowFlag(row.id)}`"
              >
                {{ rowFlag(row.id) === "new" ? "New" : "Updated" }}
              </span>
            </td>

            <td
              class="jflt__cell jflt__cell--descriptor"
              data-testid="level-descriptor"
              :title="row.descriptor"
            >
              {{ row.descriptor }}
            </td>

            <td class="jflt__cell jflt__cell--actions">
              <div v-if="!readOnly" class="jflt__overflow">
                <button
                  type="button"
                  class="jflt__icon-btn"
                  :aria-label="`Actions for ${row.name}`"
                  :aria-haspopup="'menu'"
                  :aria-expanded="openMenuId === row.id ? 'true' : 'false'"
                  :disabled="busy"
                  data-testid="row-actions"
                  @click.stop="toggleMenu(row, $event)"
                >
                  <svg
                    class="jflt__dots-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle cx="3.5" cy="10" r="1.6" fill="currentColor" />
                    <circle cx="10" cy="10" r="1.6" fill="currentColor" />
                    <circle cx="16.5" cy="10" r="1.6" fill="currentColor" />
                  </svg>
                </button>
              </div>
              <span v-else class="jflt__no-actions" aria-hidden="true" />
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="jflt__empty" role="status">
        <p class="jflt__empty__title">No levels found</p>
        <p class="jflt__empty__subtitle">
          Add a level to start building this Job Family's career ladder.
        </p>
      </div>
    </div>

    <!-- Fixed to the viewport so the table body's overflow scroll does not clip the menu. -->
    <div
      v-if="openMenuRow"
      ref="menuEl"
      class="jflt__menu jflt__menu--fixed"
      :style="menuStyle"
      role="menu"
      data-testid="row-actions-menu"
      @keydown.esc="closeMenu"
    >
      <button
        v-for="item in ROW_ACTIONS"
        :key="item.value"
        type="button"
        role="menuitem"
        class="jflt__menu-item"
        :class="{ 'jflt__menu-item--destructive': item.destructive }"
        data-testid="row-action-item"
        @click.stop="onMenuChoice(openMenuRow, item)"
      >
        {{ item.label }}
      </button>
    </div>
  </section>
</template>

<script setup>
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";

import AskIQButton from "@components/AskIQButton/AskIQButton.vue";
import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import { ROW_ACTIONS, buildDisplayOrder } from "./jobFamilyLevelsTableRow.js";

/**
 * ER-11647 — JobFamilyLevelsTable (Organization > Job Families detail view, "Levels" tab).
 *
 * Rebuilt against the Job Family Level model (rules §3, §6, §10): Levels carry no
 * status, so there is no Status column and no OrgObjectStatusBadge. Ordinals are
 * dense 1..N with Level 1 at the bottom of the ladder; the API returns Levels
 * ordinal ascending and this component reverses them for display (§3.2).
 *
 * Presentational only — the parent owns persistence. This table renders the
 * "Levels" header (title + subtitle + "+ Add Level"), and a table whose rows show
 * the positional rank, name and descriptor, plus a dots-grid drag handle and a
 * dots-horizontal overflow menu (Edit / Delete only — Levels have no
 * Activate/Retire/Reactivate/Archive/Unarchive operations).
 *
 * Reorder uses native HTML5 drag-and-drop keyed on the drag handle (same
 * interaction as reordering Checkpoints within a Responsibility). Dropping a
 * row renumbers the whole family densely and immediately, and emits `reorder`
 * with the full ordered list of Level ids (`{ orderedLevelIds }`, ordinal
 * ascending) — never a single `{ levelId, newOrder }` pair, since the backend
 * derives ordinals purely from list position. The reorder is optimistic: the
 * local row order updates on drop, and reconciles from `levels` whenever the
 * parent updates it (i.e. on success confirmation) or reverts it (on failure).
 *
 * Shipped behind the linked idea flag (idea-MDP-581, "Job Family — Discipline &
 * Career-Ladder Grouping for Positions"). Kill-switch semantics: renders by
 * default; set the flag to false to hide the table until a screen wires it.
 */
const props = defineProps({
  /** Ordinal-ascending level rows: `{ id, ordinal, name, descriptor, positionCount }`. */
  levels: {
    type: Array,
    default: () => [],
  },
  /** When true, hides drag handles, overflow menus and the Add Level action. */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /** When true (e.g. a reorder request is in flight), disables drag and row actions. */
  busy: {
    type: Boolean,
    default: false,
  },
  /** When true, shows the Post-Suggestion review banner (with Undo + close ×). */
  suggestionActive: {
    type: Boolean,
    default: false,
  },
  /** `{ [levelId]: "new" | "updated" }` — per-row IQ suggestion badges. */
  levelFlags: {
    type: Object,
    default: () => ({}),
  },
  /** When true, the Undo Suggestions request is in flight: the action is disabled. */
  undoSuggestionBusy: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "addLevel",
  "editLevel",
  "deleteLevel",
  "reorder",
  "suggest",
  "undoSuggestions",
  "dismissSuggestion",
]);

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);
const openMenuId = ref(null);
const openMenuRow = ref(null);
const menuStyle = ref({});
const menuEl = ref(null);
const draggedId = ref(null);
const dragOverId = ref(null);
const rootEl = ref(null);

const MENU_WIDTH_PX = 168;
const MENU_ITEM_HEIGHT_PX = 36;
const MENU_PADDING_PX = 8;

/** Descending display order (highest ordinal/top of the ladder first). */
const idOrder = ref([]);

/** id -> raw level fields, always sourced live from props (name/descriptor never cached). */
const levelsById = computed(() => {
  const map = new Map();
  for (const level of props.levels || []) {
    if (level && level.id !== null && level.id !== undefined) {
      map.set(level.id, level);
    }
  }
  return map;
});

/** Rows for render: id order + live fields + a dense ordinal derived from position. */
const displayRows = computed(() => {
  const total = idOrder.value.length;
  return idOrder.value
    .map((id, index) => {
      const raw = levelsById.value.get(id);
      if (!raw) return null;
      return {
        id,
        ordinal: total - index,
        name: String(raw.name || "").trim(),
        descriptor: String(raw.descriptor || "").trim(),
        positionCount: Number.isFinite(raw.positionCount) ? raw.positionCount : 0,
      };
    })
    .filter(Boolean);
});

watch(
  () => props.levels,
  (levels) => {
    idOrder.value = buildDisplayOrder(levels).map((row) => row.id);
  },
  { immediate: true, deep: true }
);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    // Kill-switch lookup failures never hide the table.
    featureEnabled.value = true;
  }
  window.addEventListener("mousedown", onOutsideMouseDown);
  window.addEventListener("keydown", onDocumentKeyDown);
  window.addEventListener("scroll", onWindowScroll, true);
});

onBeforeUnmount(() => {
  window.removeEventListener("mousedown", onOutsideMouseDown);
  window.removeEventListener("keydown", onDocumentKeyDown);
  window.removeEventListener("scroll", onWindowScroll, true);
});

function onAddLevel() {
  if (props.busy) return;
  emit("addLevel");
}

function onSuggest() {
  if (props.busy) return;
  emit("suggest");
}

function onUndoSuggestions() {
  // Never emit while an Undo request is already in flight — the parent owns the
  // request state, so this is the only thing standing between a double click and
  // two duplicate undo POSTs.
  if (props.busy || props.undoSuggestionBusy) return;
  emit("undoSuggestions");
}

function onDismissSuggestion() {
  emit("dismissSuggestion");
}

function rowFlag(levelId) {
  const flags = props.levelFlags || {};
  return flags[levelId] || null;
}

function onDragStart(event, row) {
  if (props.busy) return;
  draggedId.value = row.id;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(row.id));
  }
}

function onDragOver(row) {
  if (draggedId.value === null || draggedId.value === row.id) return;
  dragOverId.value = row.id;
}

function onDrop(row) {
  const sourceId = draggedId.value;
  draggedId.value = null;
  dragOverId.value = null;
  if (props.busy) return;
  if (sourceId === null || sourceId === undefined) return;
  if (sourceId === row.id) return;

  const current = [...idOrder.value];
  const from = current.indexOf(sourceId);
  const to = current.indexOf(row.id);
  if (from === -1 || to === -1) return;

  current.splice(from, 1);
  current.splice(to, 0, sourceId);
  idOrder.value = current;

  // idOrder is descending (top of ladder first); the API/emit contract is
  // ordinal ascending (Level 1 first), matching the GET .../levels contract.
  const orderedLevelIds = [...current].reverse();
  emit("reorder", { orderedLevelIds });
}

function onDragEnd() {
  draggedId.value = null;
  dragOverId.value = null;
}

function positionMenu(anchorEl) {
  if (!anchorEl || typeof anchorEl.getBoundingClientRect !== "function") return;
  const rect = anchorEl.getBoundingClientRect();
  const menuHeight = ROW_ACTIONS.length * MENU_ITEM_HEIGHT_PX + MENU_PADDING_PX;
  const viewportPadding = 8;
  let top = rect.bottom + 4;
  if (top + menuHeight > window.innerHeight - viewportPadding) {
    top = Math.max(viewportPadding, rect.top - menuHeight - 4);
  }
  let left = rect.right - MENU_WIDTH_PX;
  left = Math.max(viewportPadding, Math.min(left, window.innerWidth - MENU_WIDTH_PX - viewportPadding));
  menuStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
  };
}

function toggleMenu(row, event) {
  if (props.busy) return;
  if (openMenuId.value === row.id) {
    closeMenu();
    return;
  }
  openMenuId.value = row.id;
  openMenuRow.value = row;
  nextTick(() => {
    const anchor = event && event.currentTarget;
    positionMenu(anchor);
  });
}

function closeMenu() {
  openMenuId.value = null;
  openMenuRow.value = null;
  menuStyle.value = {};
}

function onWindowScroll() {
  if (openMenuId.value !== null) {
    closeMenu();
  }
}

function onMenuChoice(row, item) {
  closeMenu();
  if (item.value === "edit") {
    emit("editLevel", { levelId: row.id });
  } else if (item.value === "delete") {
    emit("deleteLevel", { levelId: row.id });
  }
}

function onOutsideMouseDown(event) {
  if (openMenuId.value === null) return;
  const target = event.target;
  const inRoot = rootEl.value && rootEl.value.contains(target);
  const inMenu = menuEl.value && menuEl.value.contains(target);
  if (!inRoot && !inMenu) {
    closeMenu();
  }
}

function onDocumentKeyDown(event) {
  if (openMenuId.value !== null && event.key === "Escape") {
    closeMenu();
  }
}
</script>

<style scoped>
.jflt {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  font-family: Inter, sans-serif;
  color: #101828;
}

/* ── Header ── */
.jflt__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.jflt__title-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.jflt__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  color: #101828;
}

.jflt__subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
  color: #475467;
}

.jflt__add {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 10px 16px;
  border: 1px solid #6845c8;
  border-radius: 8px;
  background: #6845c8;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.jflt__add:hover:not(:disabled) {
  background: #5a37ab;
  border-color: #5a37ab;
}

.jflt__add:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.jflt__add:focus-visible,
.jflt__handle:focus-visible,
.jflt__icon-btn:focus-visible,
.jflt__menu-item:focus-visible {
  outline: 2px solid #6845c8;
  outline-offset: 1px;
}

.jflt__add-icon {
  display: block;
  flex-shrink: 0;
}

/* ── Table ── */
.jflt__body {
  width: 100%;
  overflow-x: auto;
  /* ER-11825 — hug content vertically: never show an inner vertical scrollbar.
     Horizontal scroll (overflow-x: auto) is preserved for wide content. */
  overflow-y: hidden;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}

.jflt__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.jflt__col-handle {
  width: 44px;
}

.jflt__col-rank {
  width: 44px;
}

.jflt__col-name {
  width: 20%;
}

.jflt__col-descriptor {
  width: auto;
}

.jflt__col-actions {
  width: 48px;
}

.jflt__table thead th {
  padding: 12px 16px;
  border-bottom: 1px solid #eaecf0;
  background: #f9fafb;
  text-align: left;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  color: #667085;
  white-space: nowrap;
}

.jflt__th--handle,
.jflt__th--actions {
  padding: 0;
}

.jflt__table tbody td {
  padding: 14px 16px;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle;
}

.jflt__table tbody tr:last-child td {
  border-bottom: none;
}

.jflt__row--dragging td {
  opacity: 0.5;
}

.jflt__row--drag-over td {
  background: #f9f5ff;
}

.jflt__cell--handle,
.jflt__cell--actions {
  padding: 14px 12px;
  text-align: center;
}

.jflt__cell--rank {
  color: #667085;
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  font-variant-numeric: tabular-nums;
}

.jflt__cell--name {
  color: #101828;
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
  overflow-wrap: anywhere;
}

.jflt__cell--descriptor {
  max-width: 0;
  overflow: hidden;
  color: #475467;
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Row controls ── */
.jflt__handle,
.jflt__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #98a2b3;
}

/* ER-11841 — the grab cursor belongs to the drag handle only. The kebab options
   button opens a menu, so it must read as clickable (pointer), not draggable. */
.jflt__handle {
  cursor: grab;
}

.jflt__icon-btn {
  cursor: pointer;
}

.jflt__handle:hover,
.jflt__icon-btn:hover:not(:disabled) {
  background: #f2edff;
  color: #475467;
}

.jflt__handle:active {
  cursor: grabbing;
}

.jflt__handle[aria-disabled="true"],
.jflt__icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.jflt__handle-spacer,
.jflt__no-actions {
  display: inline-block;
  width: 32px;
  height: 32px;
}

.jflt__grid-icon,
.jflt__dots-icon {
  display: block;
}

.jflt__overflow {
  position: relative;
  display: inline-flex;
}

.jflt__menu {
  min-width: 168px;
  padding: 4px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03);
  text-align: left;
}

.jflt__menu--fixed {
  position: fixed;
  z-index: 2500;
}

.jflt__menu-item {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: #344054;
  cursor: pointer;
}

.jflt__menu-item:hover {
  background: #f9fafb;
}

.jflt__menu-item--destructive {
  color: #d92d20;
}

.jflt__menu-item--destructive:hover {
  background: #fef3f2;
}

/* ── Empty state ── */
.jflt__empty {
  padding: 40px 24px;
  text-align: center;
}

.jflt__empty__title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.jflt__empty__subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.jflt__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Header actions (Add Level + Suggest) ── */
.jflt__actions {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

/* The shared AskIQButton ships with a 200px min-width; the Suggest control in the
   design hugs its content alongside "+ Add Level", so constrain it locally. */
.jflt__suggest::v-deep .ask-iq-button {
  min-width: 0;
}

/* ── Post-Suggestion review banner ── */
.jflt__suggestion-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #ffffff;
}

.jflt__suggestion-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.jflt__suggestion-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.jflt__suggestion-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.jflt__suggestion-subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.jflt__suggestion-undo {
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #6941c6;
  cursor: pointer;
}

.jflt__suggestion-undo:hover:not(:disabled) {
  text-decoration: underline;
}

/* Visible feedback while the undo request is in flight. */
.jflt__suggestion-undo:disabled {
  color: #98a2b3;
  cursor: default;
}

.jflt__suggestion-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  cursor: pointer;
}

.jflt__suggestion-close:hover {
  background: #f2f4f7;
}

.jflt__suggestion-undo:focus-visible,
.jflt__suggestion-close:focus-visible {
  outline: 2px solid #6845c8;
  outline-offset: 1px;
}

/* ── Per-row IQ badges (New / Updated) ── */
.jflt__name-text {
  margin-right: 8px;
}

.jflt__badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 2px 8px;
  border: 1px solid #e9d7fe;
  border-radius: 9999px;
  background: #f9f5ff;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #6941c6;
  white-space: nowrap;
}
</style>
